import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sunrise, Sunset, Star, Calendar, Moon } from 'lucide-react';
import { getPanchangData } from '../services/panchangApi';

const HomePanchangSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTodayPanchang = async () => {
      try {
        const today = new Date();
        const dateStr = today.toISOString().split('T')[0];
        const result = await getPanchangData(dateStr);
        setData(result);
      } catch (err) {
        console.error("Failed to fetch today's panchang:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTodayPanchang();
  }, []);

  if (loading || !data) {
    return (
      <section className="py-8 mb-12 bg-white rounded-3xl shadow-sm border border-stone-200 max-w-[1200px] mx-auto animate-pulse flex items-center justify-center min-h-[160px]">
        <div className="flex space-x-2 items-center">
            <div className="w-4 h-4 bg-saffron-300 rounded-full"></div>
            <div className="w-4 h-4 bg-saffron-300 rounded-full"></div>
            <div className="w-4 h-4 bg-saffron-300 rounded-full"></div>
        </div>
      </section>
    );
  }

  const todayStr = new Date().toLocaleDateString('hi-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <section className="py-8 md:py-10 mb-12 bg-white rounded-3xl shadow-sm border border-stone-200 overflow-hidden relative max-w-[1200px] mx-auto">
      <div className="absolute top-0 right-0 w-64 h-64 bg-saffron-50 rounded-full blur-3xl opacity-60 -mr-16 -mt-16 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-50 rounded-full blur-3xl opacity-60 -ml-16 -mb-16 pointer-events-none"></div>
      
      <div className="px-6 md:px-10 relative z-10">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4 border-b border-stone-100 pb-6">
          <div>
            <h2 className="text-3xl font-extrabold text-stone-800 flex items-center tracking-tight">
              <span className="text-saffron-600 mr-3 text-4xl">🕉️</span> आज का पंचांग
            </h2>
            <p className="text-stone-500 font-medium mt-2 text-sm md:text-base flex items-center">
              <span className="inline-block w-2 h-2 rounded-full bg-saffron-500 mr-2"></span>
              {todayStr} • {data.vaara.hi}
            </p>
          </div>
          <div className="flex flex-col items-start md:items-end w-full md:w-auto mt-2 md:mt-0">
             <p className="text-xs text-stone-500 mb-3 hidden md:block">आज का संपूर्ण पंचांग, शुभ मुहूर्त, तिथि, नक्षत्र और अन्य जानकारी।</p>
            <Link to="/panchang-2026" className="inline-flex items-center px-6 py-2.5 bg-saffron-600 text-white font-bold rounded-full hover:bg-saffron-700 transition-colors shadow-md whitespace-nowrap group w-full md:w-auto justify-center">
              पूर्ण हिंदी पंचांग देखें <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
          {/* Tithi */}
          <div className="bg-stone-50/80 backdrop-blur-sm p-4 md:p-5 rounded-2xl border border-stone-100 hover:border-saffron-300 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group">
            <div className="flex items-center mb-3">
              <div className="p-2 bg-white rounded-lg shadow-sm mr-3">
                <Calendar className="w-5 h-5 text-saffron-500 group-hover:scale-110 transition-transform" />
              </div>
              <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">आज की तिथि</span>
            </div>
            <p className="font-extrabold text-stone-800 text-sm md:text-base leading-snug">{data.tithi.name.hi.split(' - ')[1] || data.tithi.name.hi}</p>
          </div>

          {/* Paksha */}
          <div className="bg-stone-50/80 backdrop-blur-sm p-4 md:p-5 rounded-2xl border border-stone-100 hover:border-saffron-300 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group">
            <div className="flex items-center mb-3">
              <div className="p-2 bg-white rounded-lg shadow-sm mr-3">
                <Moon className="w-5 h-5 text-blue-500 group-hover:scale-110 transition-transform" />
              </div>
              <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">पक्ष</span>
            </div>
            <p className="font-extrabold text-stone-800 text-sm md:text-base">{data.tithi.paksha === "Shukla" ? "शुक्ल पक्ष" : "कृष्ण पक्ष"}</p>
          </div>

          {/* Nakshatra */}
          <div className="bg-stone-50/80 backdrop-blur-sm p-4 md:p-5 rounded-2xl border border-stone-100 hover:border-saffron-300 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group">
            <div className="flex items-center mb-3">
              <div className="p-2 bg-white rounded-lg shadow-sm mr-3">
                <Star className="w-5 h-5 text-purple-500 group-hover:scale-110 transition-transform" />
              </div>
              <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">नक्षत्र</span>
            </div>
            <p className="font-extrabold text-stone-800 text-sm md:text-base">{data.nakshatra.name.hi}</p>
          </div>

          {/* Sunrise */}
          <div className="bg-stone-50/80 backdrop-blur-sm p-4 md:p-5 rounded-2xl border border-stone-100 hover:border-saffron-300 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group">
            <div className="flex items-center mb-3">
              <div className="p-2 bg-white rounded-lg shadow-sm mr-3">
                <Sunrise className="w-5 h-5 text-amber-500 group-hover:scale-110 transition-transform" />
              </div>
              <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">सूर्योदय</span>
            </div>
            <p className="font-extrabold text-stone-800 text-sm md:text-base">{data.timings.sunrise}</p>
          </div>

          {/* Sunset */}
          <div className="bg-stone-50/80 backdrop-blur-sm p-4 md:p-5 rounded-2xl border border-stone-100 hover:border-saffron-300 hover:shadow-md hover:-translate-y-1 transition-all duration-300 group">
            <div className="flex items-center mb-3">
               <div className="p-2 bg-white rounded-lg shadow-sm mr-3">
                <Sunset className="w-5 h-5 text-orange-500 group-hover:scale-110 transition-transform" />
              </div>
              <span className="text-xs font-bold text-stone-500 uppercase tracking-wider">सूर्यास्त</span>
            </div>
            <p className="font-extrabold text-stone-800 text-sm md:text-base">{data.timings.sunset}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomePanchangSection;
